import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/auth-context';
import { Avatar } from '../shared/Avatar';
import { Button } from '../shared/Button';
import { Icon } from '../shared/Icons';

// Role → the label shown under the user's name. The DB values are snake_case identifiers; nobody
// should read "federation_admin" in a UI.
const ROLE_LABEL: Record<string, string> = {
  referee: 'Referee',
  federation_admin: 'Federation admin',
  super_admin: 'ProRef admin',
};

// The topbar account control. Replaces the bare Sign-out button that sat here: with /settings
// existing there is now more than one thing to do with your account, and a row of top-level
// buttons is the wrong shape for that — signing out is also the one action here you least want
// adjacent to a mis-tap.
export function UserMenu() {
  const [open, setOpen] = useState(false);
  const { user, role, logout } = useAuth();
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);

  // Escape + click-outside, matching NotificationBell so both topbar dropdowns behave alike.
  useEffect(() => {
    if (!open) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onPointerDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  async function handleSignOut() {
    setOpen(false);
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="relative flex-none" ref={wrapRef}>
      <Button
        variant="ghost"
        size="sm"
        className="flex-none"
        onClick={() => setOpen((v) => !v)}
        title="Account"
        aria-label="Account menu"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Avatar name={user?.full_name ?? '?'} size="sm" />
        {/* The name is the first thing worth dropping when the topbar runs out of width — the
            avatar alone still reads as "your account". */}
        <span className="hidden max-w-[140px] truncate sm:inline">{user?.full_name ?? 'Account'}</span>
        <Icon.chevronRight className="w-3 h-3 rotate-90 text-muted" />
      </Button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 top-11 z-40 w-[min(240px,calc(100vw-2rem))] overflow-hidden rounded-md border border-line-2 bg-surface shadow-2"
        >
          <div className="border-b border-line px-3 py-2.5">
            <p className="truncate text-[13px] font-medium text-ink">{user?.full_name ?? '—'}</p>
            <p className="truncate text-[11px] text-muted">{user?.email ?? ''}</p>
            {role && <p className="mt-1 text-[11px] text-muted-2">{ROLE_LABEL[role] ?? role}</p>}
          </div>

          <Link
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-ink transition-colors hover:bg-surface-2"
          >
            <Icon.filter className="w-[14px] h-[14px] text-muted" />
            Settings
          </Link>

          {/* Separated from Settings by its own border: sign-out is the one destructive-ish action
              in this menu, and it should not sit flush against a routine one. */}
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            className="flex w-full items-center gap-2.5 border-t border-line px-3 py-2.5 text-left text-[13px] text-ink transition-colors hover:bg-surface-2"
          >
            <Icon.logout className="w-[14px] h-[14px] text-muted" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
